import { useState, useEffect, useRef, useCallback } from "react";

const ASCII_RAMP =
  "$@B%8&WM#*oahkbdpqwmZO0QLCJUYXzcvunxrjft/\\|()1{}[]?-_+~<>i!lI;:,\".,'";

const FADE_MS = 400;

/* ---------- ASCII GENERATION ---------- */

function imageToColoredAscii(img, cols) {
  if (!img.naturalWidth || !img.naturalHeight) return null;

  const offscreen = document.createElement("canvas");
  const ctx = offscreen.getContext("2d");

  const aspect = img.naturalHeight / img.naturalWidth;
  const rows = Math.floor(cols * aspect * 0.45);

  offscreen.width = cols;
  offscreen.height = rows;

  ctx.drawImage(img, 0, 0, cols, rows);

  let data;
  try {
    data = ctx.getImageData(0, 0, cols, rows).data;
  } catch (e) {
    console.error("Canvas read failed (CORS?)", e);
    return null;
  }

  const chars = [];
  const colors = [];

  for (let y = 0; y < rows; y++) {
    const rowChars = [];
    const rowColors = [];

    for (let x = 0; x < cols; x++) {
      const i = (y * cols + x) * 4;
      const r = data[i], g = data[i + 1], b = data[i + 2];

      const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      const idx = Math.floor(brightness * (ASCII_RAMP.length - 1));

      rowChars.push(ASCII_RAMP[idx]);
      rowColors.push([r, g, b]);
    }

    chars.push(rowChars);
    colors.push(rowColors);
  }

  return { chars, colors, rows };
}

/* ---------- RENDER ---------- */

function renderColoredAscii(canvas, asciiData, containerWidth, cols) {
  const { chars, colors, rows } = asciiData;

  const fontSize = containerWidth / (cols * 0.6);
  const lineHeight = fontSize * 1.2;
  const cw = fontSize * 0.6;

  canvas.width = Math.ceil(containerWidth);
  canvas.height = Math.ceil(rows * lineHeight);

  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.font = `${fontSize}px monospace`;
  ctx.textBaseline = "top";

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const [r, g, b] = colors[y][x];
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.fillText(chars[y][x], x * cw, y * lineHeight);
    }
  }
}

/* ---------- COMPONENT ---------- */

export default function AsciiImageViewer({
  src,
  alt = "Image",
  cols = 240,
  redirectTo = "404",
  mode = null, // null = hover, 0 = image only, 1 = ascii only
}) {
  const [asciiData, setAsciiData] = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [skeletonVisible, setSkeletonVisible] = useState(true);

  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const skeletonTimerRef = useRef(null);

  /* ---------- RESET ---------- */
  useEffect(() => {
    setImgLoaded(false);
    setAsciiData(null);
    setHovered(false);
    setShowSkeleton(true);
    setSkeletonVisible(true);

    if (skeletonTimerRef.current) {
      clearTimeout(skeletonTimerRef.current);
    }
  }, [src, mode]);

  /* ---------- LOAD ---------- */
  const handleLoad = useCallback(async () => {
    const img = imgRef.current;
    if (!img) return;

    try {
      if (img.decode) await img.decode();
    } catch {}

    if (!img.naturalWidth) return;

    setImgLoaded(true);

    skeletonTimerRef.current = setTimeout(() => {
      setShowSkeleton(false);
      setTimeout(() => {
        setSkeletonVisible(false);
      }, FADE_MS);
    }, 250);

    if (mode === 0) return;

    requestAnimationFrame(() => {
      const ascii = imageToColoredAscii(img, cols);
      if (ascii) {
        setAsciiData(ascii);
      }
    });
  }, [cols, mode]);

  /* ---------- RENDER ASCII ---------- */
  useEffect(() => {
    if (!asciiData || !canvasRef.current || !containerRef.current) return;

    const w = containerRef.current.offsetWidth;
    if (!w) return;

    renderColoredAscii(canvasRef.current, asciiData, w, cols);
  }, [asciiData, cols]);

  /* ---------- DISPLAY LOGIC ---------- */
  const showAscii =
    mode === 1
      ? !!asciiData
      : mode === 0
      ? false
      : hovered && !!asciiData;

  /* ---------- JSX ---------- */
  return (
    <div
      ref={containerRef}
      onMouseEnter={() => {
        if (mode === null) setHovered(true);
      }}
      onMouseLeave={() => {
        if (mode === null) setHovered(false);
      }}
      {...(redirectTo !== "404"
        ? {
            onClick: () => {
              window.location.pathname = `/r/${redirectTo}`;
            },
          }
        : {})}
      style={{
        position: "relative",
        width: "100%",
        overflow: "hidden",
        cursor: redirectTo === "404" ? "crosshair" : "pointer",
      }}
    >
      {/* Skeleton with fade */}
      {skeletonVisible && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "#e5e5e5",
            opacity: showSkeleton ? 1 : 0,
            transition: `opacity ${FADE_MS}ms`,
          }}
        />
      )}

      <img
        ref={imgRef}
        src={src}
        alt={alt}
        crossOrigin="anonymous"
        onLoad={handleLoad}
        style={{
          width: "100%",
          display: "block",
          opacity: showAscii || mode === 1 ? 0 : 1,
          transition: `opacity ${FADE_MS}ms`,
        }}
      />

      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: showAscii ? 1 : 0,
          transition: `opacity ${FADE_MS}ms`,
          pointerEvents: "none",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            width: "100%",
            height: "100%",
            display: "block",
          }}
        />
      </div>
    </div>
  );
}